// src/utils/sheetRouter.js
//
// Dynamic multi-sheet routing for the OTTI workbook upload. The analyst's
// "OTTI Watchlist YYYYMMDD.xlsx" master ships 8 sheets; only three feed the
// pipeline and each lands at a different destination:
//
//   - lot_detail        ("Lot Level Detail" / "Summary") -> grid merge + /save-otti
//   - current_spread    ("Current Spread")               -> /upload-current-spreads
//   - ratings_downgrades("Ratings Downgrades")           -> /upload-ratings-downgrades
//
// The reference sheets ("Analytics By Lot", "Intex Assumptions", "Static
// Assumptions", "Explanation of Testing") are already in Snowflake / are
// reference material and are skipped.
//
// All functions here are pure: they take the raw `{ sheetNames, sheetsData }`
// shape returned by @1823-partners/core's `parseExcelWorkbook` (sheetsData[name]
// is a 2D array, header:1) and return plain data structures. No I/O.
import { coerceNumeric, coerceDate } from '@1823-partners/core';

// Each routed type carries the sheet name(s) it is known by, plus a header
// "signature": the column labels we expect on the header row. The signature is
// the fallback when a sheet has been renamed — we still recognize it by shape.
// `headerHint` is the 0-based row index where the header usually sits (the
// workbooks have title/preamble rows above some headers); it is only a hint —
// `findHeaderRow` always scans.
export const SHEET_DEFS = [
  {
    type: 'lot_detail',
    names: ['Lot Level Detail', 'Summary'],
    headerHint: 0,
    // Subset of the ~40 Lot Level Detail columns — enough to fingerprint it
    // without depending on the optional/hidden analytic columns.
    signature: ['LOT ID', 'SRC LOT ID', 'ID', 'DES', 'SEC OWNER', 'STYPEL3'],
  },
  {
    type: 'current_spread',
    names: ['Current Spread'],
    headerHint: 1,
    signature: ['ID', 'Description', 'Sec Owner', 'Market Price', 'SPREAD'],
  },
  {
    type: 'ratings_downgrades',
    names: ['Ratings Downgrades'],
    headerHint: 6,
    signature: ['Account', 'Identifier', 'Agency', 'Old Value', 'New Value', 'Event Type'],
  },
];

// The workbook's reference / Snowflake-backed sheets. They are always skipped:
// "Analytics By Lot" carries lot-keyed columns, so the signature fallback
// alone would route it as lot_detail and turn reference data into edits.
export const REFERENCE_SHEET_NAMES = [
  'Analytics By Lot',
  'Intex Assumptions',
  'Static Assumptions',
  'Explanation of Testing',
];

// How many leading rows to scan when hunting for the header row.
const HEADER_SCAN_ROWS = 12;
// Fraction of signature columns that must be present for a row / sheet to match.
const SIGNATURE_MATCH_RATIO = 0.6;

const norm = (value) => String(value == null ? '' : value).trim().toLowerCase();

// Count how many of `signatureCols` appear (case-insensitive, trimmed) in `cells`.
const countSignatureHits = (cells, signatureCols) => {
  const present = new Set((cells || []).map(norm).filter(Boolean));
  return signatureCols.reduce((hits, col) => (present.has(norm(col)) ? hits + 1 : hits), 0);
};

/**
 * Find the header row of a sheet by scanning the first ~12 rows for the row
 * that contains the most signature columns (and at least SIGNATURE_MATCH_RATIO
 * of them). Handles workbooks with title / preamble rows above the header.
 *
 * @param {Array<Array>} rows2d - 2D array of cell values (header:1 shape).
 * @param {Array<string>} signatureCols - Expected header column labels.
 * @returns {number} 0-based index of the header row, or -1 if none qualifies.
 */
export const findHeaderRow = (rows2d, signatureCols) => {
  if (!Array.isArray(rows2d) || rows2d.length === 0) return -1;
  if (!Array.isArray(signatureCols) || signatureCols.length === 0) return -1;

  const threshold = Math.max(1, Math.ceil(signatureCols.length * SIGNATURE_MATCH_RATIO));
  const limit = Math.min(rows2d.length, HEADER_SCAN_ROWS);

  let bestIdx = -1;
  let bestHits = 0;
  for (let i = 0; i < limit; i += 1) {
    const hits = countSignatureHits(rows2d[i], signatureCols);
    if (hits >= threshold && hits > bestHits) {
      bestHits = hits;
      bestIdx = i;
    }
  }
  return bestIdx;
};

// Determine the routed type for a sheet: name match first (case-insensitive,
// trimmed), then column-signature fallback against the 2D rows. Returns an
// object { def, matchedByName } or null. `matchedByName` lets the caller fall
// back to the def's headerHint when a name-matched sheet has a thinner-than-
// expected header that the signature scan can't pin down.
const matchSheetDef = (sheetName, rows2d) => {
  const nName = norm(sheetName);

  // 1) Exact name match (case-insensitive, trimmed).
  const byName = SHEET_DEFS.find((def) => def.names.some((n) => norm(n) === nName));
  if (byName) return { def: byName, matchedByName: true };

  // A known reference sheet is never routed, whatever its columns look like.
  if (REFERENCE_SHEET_NAMES.some((n) => norm(n) === nName)) return null;

  // 2) Signature fallback: a header row exists somewhere in the sheet that
  //    matches enough of a def's signature.
  for (const def of SHEET_DEFS) {
    if (findHeaderRow(rows2d, def.signature) !== -1) {
      return { def, matchedByName: false };
    }
  }
  return null;
};

// Convert the data rows below `headerIdx` into objects keyed by the header
// labels. Trailing empty header cells are skipped; fully-empty rows are dropped.
const rowsToObjects = (rows2d, headerIdx) => {
  const header = (rows2d[headerIdx] || []).map((cell) => String(cell == null ? '' : cell).trim());
  const out = [];
  for (let i = headerIdx + 1; i < rows2d.length; i += 1) {
    const row = rows2d[i] || [];
    const allEmpty = row.every((cell) => cell === '' || cell === null || cell === undefined);
    if (allEmpty) continue;
    const obj = {};
    header.forEach((key, col) => {
      if (key) obj[key] = row[col] !== undefined ? row[col] : '';
    });
    out.push(obj);
  }
  return out;
};

/**
 * Detect and parse every sheet in a parsed workbook.
 *
 * @param {Object} parsed - The `{ sheetNames, sheetsData }` shape from
 *   `parseExcelWorkbook` (sheetsData[name] is a 2D array).
 * @returns {Array<{sheetName: string, type: string, rows: Array<Object>}>}
 *   One entry per sheet. Matched sheets carry their routed `type` and the
 *   parsed row objects; unmatched sheets are `type: 'unknown'` with `rows: []`.
 */
export const detectAndParse = ({ sheetNames = [], sheetsData = {} } = {}) => {
  return sheetNames.map((sheetName) => {
    const rows2d = sheetsData[sheetName] || [];
    const match = matchSheetDef(sheetName, rows2d);
    if (!match) {
      return { sheetName, type: 'unknown', rows: [] };
    }
    const { def, matchedByName } = match;

    let headerIdx = findHeaderRow(rows2d, def.signature);
    if (headerIdx === -1 && matchedByName) {
      // Name-matched but the signature scan couldn't pin the header (e.g. a
      // file with a reduced column set). Fall back to the def's headerHint as
      // long as that row carries something — better to import the named sheet
      // than to drop it.
      const hint = def.headerHint || 0;
      const hintRow = rows2d[hint] || [];
      const hasContent = hintRow.some((cell) => String(cell == null ? '' : cell).trim() !== '');
      if (hasContent) headerIdx = hint;
    }
    if (headerIdx === -1) {
      // Could not locate a header -> treat as unknown so the caller skips it
      // with a notice rather than uploading garbage.
      return { sheetName, type: 'unknown', rows: [] };
    }
    return { sheetName, type: def.type, rows: rowsToObjects(rows2d, headerIdx) };
  });
};

// --- Payload mapping helpers -------------------------------------------------
//
// These turn the detected row objects into the exact upload contracts the
// backend expects. ASOFDT is the app's selected date ('YYYY-MM-DD').

/**
 * Build the /upload-current-spreads payload from "Current Spread" rows.
 * Contract: { CUSIP, ASOFDT, MARKET_PRICE, SPREAD, PRICE_SOURCE, WAL, YIELD }.
 * Only rows with a CUSIP (ID column) AND a numeric SPREAD are kept.
 *
 * @param {Array<Object>} rows - Objects keyed by the Current Spread header.
 * @param {string} asofdt - 'YYYY-MM-DD'.
 * @returns {Array<Object>}
 */
export const mapCurrentSpreadPayload = (rows, asofdt) => {
  return (rows || [])
    .map((row) => {
      const cusip = String(row.ID == null ? '' : row.ID).trim();
      const spread = coerceNumeric(row.SPREAD);
      if (!cusip || spread === null) return null;
      return {
        CUSIP: cusip,
        ASOFDT: asofdt,
        MARKET_PRICE: coerceNumeric(row['Market Price']),
        SPREAD: spread,
        PRICE_SOURCE: row['Price Source'] != null ? String(row['Price Source']).trim() : '',
        // The Current Spread header is literally 'WAL ' (trailing space); accept
        // both for robustness.
        WAL: coerceNumeric(row.WAL != null ? row.WAL : row['WAL ']),
        YIELD: coerceNumeric(row.YIELD),
      };
    })
    .filter(Boolean);
};

// A row is a downgrade if its Event Type contains 'downgrade' (case-insensitive).
const isDowngrade = (row) => norm(row['Event Type']).includes('downgrade');

/**
 * Build the /upload-ratings-downgrades payload from "Ratings Downgrades" rows.
 * Contract: { CUSIP, ASOFDT, AGENCY, OLD_VALUE, NEW_VALUE, EVENT_TYPE }.
 * Only rows whose Event Type contains 'Downgrade' AND that have a CUSIP
 * (Identifier column) are kept.
 *
 * @param {Array<Object>} rows - Objects keyed by the Ratings Downgrades header.
 * @param {string} asofdt - 'YYYY-MM-DD'.
 * @returns {Array<Object>}
 */
export const mapRatingsDowngradePayload = (rows, asofdt) => {
  return (rows || [])
    .filter(isDowngrade)
    .map((row) => {
      const cusip = String(row.Identifier == null ? '' : row.Identifier).trim();
      if (!cusip) return null;
      return {
        CUSIP: cusip,
        ASOFDT: asofdt,
        AGENCY: row.Agency != null ? String(row.Agency).trim() : '',
        OLD_VALUE: row['Old Value'] != null ? String(row['Old Value']).trim() : '',
        NEW_VALUE: row['New Value'] != null ? String(row['New Value']).trim() : '',
        EVENT_TYPE: row['Event Type'] != null ? String(row['Event Type']).trim() : '',
      };
    })
    .filter(Boolean);
};

// Re-export coerceDate so consumers that need it for lot_detail date handling
// can pull it from the router alongside the mappers (keeps the import surface
// in one place); currently unused by the mappers above but part of the helper
// contract described in the plan.
export { coerceDate };
